import { useState, useCallback } from 'react';

import { useModifyPasswordMutation } from '@features/user/hook';

import Alert from '@shared/components/atoms/Alert';
import Button from '@shared/components/atoms/Button';
import { useAlert } from '@shared/hooks';
import eyeClosedIcon from '@shared/assets/images/eye-closed_icon.svg';
import eyeOpenIcon from '@shared/assets/images/eye-open_icon.svg';

import usePasswordChangeForm from '@features/user/hook/usePasswordChangeForm';

import styles from './MobilePasswordSettingContent.module.scss';

/**
 * 모바일 비밀번호 설정 콘텐츠 컴포넌트
 * - Figma 디자인 기반: 비밀번호 변경 기능
 */
function MobilePasswordSettingContent() {
  const {
    formData,
    showPassword,
    alertState,
    isPending,
    handleInputChange,
    toggleShowPassword,
    isPasswordMismatch,
    submit,
    closeAlert,
  } = usePasswordChangeForm();

  const handleSubmit = useCallback(
    function (e) {
      e.preventDefault();
      submit();
    },
    [submit],
  );

  return (
    <>
      <div className={styles.container}>
        {/* 제목 */}
        <h2 className={styles.title}>비밀번호 설정</h2>

        <form onSubmit={handleSubmit} name="passwordChangeForm" id="passwordChangeForm">
          {/* 접근성을 위한 숨겨진 사용자명 필드 */}
          <input
            type="text"
            autoComplete="username"
            className={styles.hiddenUsernameField}
            tabIndex={-1}
            aria-hidden="true"
          />
          {/* 비밀번호 생성 규칙 안내 */}
          <div className={styles.passwordRule}>
            <span className={styles.ruleBullet}>•</span>
            <span className={styles.ruleText}>영문, 숫자, 특수문자 혼합 8자 이상</span>
          </div>

          {/* 비밀번호 입력 섹션 */}
          <div className={styles.passwordSection}>
            {/* 현재 비밀번호 (container : file1) */}
            <div className={styles.inputRow}>
              <div className={styles.inputWrapper}>
                <input
                  type={showPassword.current ? 'text' : 'password'}
                  name="currentPassword"
                  className={styles.input}
                  value={formData.currentPassword}
                  onChange={handleInputChange('currentPassword')}
                  placeholder="현재 비밀번호"
                  autoComplete="current-password"
                  form="passwordChangeForm"
                />
                <button
                  type="button"
                  className={styles.eyeButton}
                  onClick={toggleShowPassword('current')}
                  aria-label={showPassword.current ? '비밀번호 숨기기' : '비밀번호 보기'}
                >
                  <img
                    src={showPassword.current ? eyeOpenIcon : eyeClosedIcon}
                    alt=""
                    className={styles.eyeIcon}
                  />
                </button>
              </div>
            </div>

            {/* 새로운 비밀번호 그룹 (Frame 2085663774) */}
            <div className={styles.newPasswordGroup}>
              {/* 새로운 비밀번호 */}
              <div className={styles.inputRow}>
                <div className={styles.inputWrapper}>
                  <input
                    type={showPassword.new ? 'text' : 'password'}
                    className={styles.input}
                    value={formData.newPassword}
                    onChange={handleInputChange('newPassword')}
                    placeholder="새로운 비밀번호"
                    autoComplete="new-password"
                    form="passwordChangeForm"
                  />
                  <button
                    type="button"
                    className={styles.eyeButton}
                    onClick={toggleShowPassword('new')}
                    aria-label={showPassword.new ? '비밀번호 숨기기' : '비밀번호 보기'}
                  >
                    <img
                      src={showPassword.new ? eyeOpenIcon : eyeClosedIcon}
                      alt=""
                      className={styles.eyeIcon}
                    />
                  </button>
                </div>
              </div>

              {/* 새로운 비밀번호 확인 */}
              <div className={styles.inputRow}>
                <div className={styles.inputWrapper}>
                  <input
                    type={showPassword.confirm ? 'text' : 'password'}
                    name="confirmPassword"
                    className={`${styles.input} ${isPasswordMismatch() ? styles.inputError : ''}`}
                    value={formData.confirmPassword}
                    onChange={handleInputChange('confirmPassword')}
                    placeholder="새로운 비밀번호 확인"
                    autoComplete="new-password"
                    form="passwordChangeForm"
                  />
                  <button
                    type="button"
                    className={styles.eyeButton}
                    onClick={toggleShowPassword('confirm')}
                    aria-label={showPassword.confirm ? '비밀번호 숨기기' : '비밀번호 보기'}
                  >
                    <img
                      src={showPassword.confirm ? eyeOpenIcon : eyeClosedIcon}
                      alt=""
                      className={styles.eyeIcon}
                    />
                  </button>
                </div>
                {/* 에러 메시지 */}
                {isPasswordMismatch() && (
                  <div className={styles.errorMessage}>비밀번호가 일치하지 않습니다.</div>
                )}
              </div>
            </div>
          </div>

          {/* 변경 버튼 */}
          <div className={styles.buttonGroup}>
            <Button
              type="submit"
              variant="default"
              className={styles.changeButton}
              disabled={isPending}
            >
              {isPending ? '변경 중...' : '변경하기'}
            </Button>
          </div>
        </form>
      </div>

      {/* Alert 컴포넌트 */}
      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
    </>
  );
}

export default MobilePasswordSettingContent;
